/**
 * Classroom analytics.
 *
 * A teacher with thirty students does not need thirty dashboards. They need the
 * answer to one question — **who is stuck, and on what?** — and they need it
 * before the lesson starts, not in a report at the end of term.
 *
 * That question is a fan-out over the same concept graph the student side uses.
 * A whole class struggling with one concept is a *teaching* signal: it means the
 * lesson did not land, and no amount of individual practice fixes it. One
 * student struggling alone is a different problem with a different response.
 * These functions separate the two.
 *
 * Pure, like everything else in this package: `now` is injected, and there is no
 * React, no network and no storage here.
 */

import { allPrerequisites, type CurriculumRegistry } from '@yuan-math/curriculum';
import { aggregateMastery, computeMastery, MASTERY_THRESHOLD, type MasteryBreakdown } from './mastery.ts';
import { isDue } from './review.ts';
import type { LearnerProfile } from './types.ts';

/** One student as the teacher sees them. Never the raw profile. */
export type ClassMember = {
  profileId: string;
  displayName: string;
  profile: LearnerProfile;
};

export type ClassRoster = {
  id: string;
  name: string;
  /** Concepts the teacher is currently teaching. Focuses every view below. */
  focusConceptIds: string[];
  members: ClassMember[];
};

/* -------------------------------------------------------------------------- */
/* Per-student summary                                                        */
/* -------------------------------------------------------------------------- */

export type StudentSummary = {
  profileId: string;
  displayName: string;
  /** Mastery across the class's focus concepts. */
  focusMastery: number;
  /** Concepts at or above the mastery line. */
  masteredCount: number;
  /** Focus concepts started but well short of mastery. */
  strugglingConceptIds: string[];
  /** Concepts due for review that the student has not returned to. */
  overdueCount: number;
  attemptsThisWeek: number;
  accuracy: number | undefined;
  /** Milliseconds since the last graded attempt, or undefined if never. */
  inactiveFor: number | undefined;
};

const WEEK_MS = 7 * 86_400_000;

/** Below this on a started concept, a student is struggling rather than learning. */
export const STRUGGLING_THRESHOLD = 0.5;

export function summariseStudent(
  member: ClassMember,
  focusConceptIds: readonly string[],
  now: number,
): StudentSummary {
  const { profile } = member;

  const struggling = focusConceptIds.filter((conceptId) => {
    const state = profile.concepts[conceptId];
    if (!state || state.attempts < 3) return false;
    return computeMastery(state, now).overall < STRUGGLING_THRESHOLD;
  });

  const mastered = focusConceptIds.filter(
    (conceptId) => computeMastery(profile.concepts[conceptId], now).overall >= MASTERY_THRESHOLD,
  );

  const overdue = Object.values(profile.concepts).filter((state) => isDue(state, now)).length;

  const graded = profile.attempts.filter((attempt) => attempt.status !== 'malformed');
  const recent = graded.filter((attempt) => now - attempt.at <= WEEK_MS);
  const lastAttempt = graded[graded.length - 1];

  return {
    profileId: member.profileId,
    displayName: member.displayName,
    focusMastery: aggregateMastery(focusConceptIds, profile.concepts, now),
    masteredCount: mastered.length,
    strugglingConceptIds: struggling,
    overdueCount: overdue,
    attemptsThisWeek: recent.length,
    accuracy: recent.length === 0 ? undefined : recent.filter((a) => a.correct).length / recent.length,
    inactiveFor: lastAttempt ? now - lastAttempt.at : undefined,
  };
}

/* -------------------------------------------------------------------------- */
/* Who needs attention                                                        */
/* -------------------------------------------------------------------------- */

export type AttentionReason =
  /** Working hard and getting it wrong — the most urgent case. */
  | 'struggling'
  /** A prerequisite is missing underneath the current work. */
  | 'missing-foundation'
  /** Has not practised in a while. */
  | 'inactive'
  /** Accurate and fast on everything set; needs harder work. */
  | 'ready-for-more';

export type AttentionFlag = {
  profileId: string;
  displayName: string;
  reason: AttentionReason;
  /** One sentence a teacher can act on without opening anything else. */
  detail: string;
  /** Higher sorts first. */
  priority: number;
  /** What to look at, when the reason names a concept. */
  conceptId?: string;
};

export const INACTIVE_DAYS = 7;

/**
 * The list a teacher reads first.
 *
 * Ordered by urgency, and deliberately capped by the caller: a list of thirty
 * flags is a list nobody reads. Each flag names a specific next action rather
 * than a score.
 */
export function studentsNeedingAttention(
  roster: ClassRoster,
  registry: CurriculumRegistry,
  now: number,
  limit = 8,
): AttentionFlag[] {
  const flags: AttentionFlag[] = [];

  for (const member of roster.members) {
    const summary = summariseStudent(member, roster.focusConceptIds, now);

    // A missing prerequisite outranks the struggle it is causing: practising
    // the harder concept cannot fix a hole underneath it.
    for (const conceptId of summary.strugglingConceptIds) {
      const weakest = weakestPrerequisite(member.profile, registry, conceptId, now);
      if (weakest) {
        flags.push({
          profileId: member.profileId,
          displayName: member.displayName,
          reason: 'missing-foundation',
          conceptId: weakest.conceptId,
          detail: `Struggling with ${title(registry, conceptId)} because ${title(registry, weakest.conceptId)} is not secure (${Math.round(weakest.mastery * 100)}%).`,
          priority: 100 + (1 - weakest.mastery) * 20,
        });
      } else {
        flags.push({
          profileId: member.profileId,
          displayName: member.displayName,
          reason: 'struggling',
          conceptId,
          detail: `Struggling with ${title(registry, conceptId)}, but the prerequisites are solid — this needs teaching, not more practice.`,
          priority: 90,
        });
      }
    }

    const inactiveDays = summary.inactiveFor === undefined ? undefined : summary.inactiveFor / 86_400_000;
    if (summary.attemptsThisWeek === 0 && inactiveDays !== undefined && inactiveDays >= INACTIVE_DAYS) {
      flags.push({
        profileId: member.profileId,
        displayName: member.displayName,
        reason: 'inactive',
        detail: `No practice for ${Math.floor(inactiveDays)} days.`,
        priority: 60,
      });
    }

    // Worth surfacing: a bored student is a problem a teacher can also solve.
    if (
      summary.strugglingConceptIds.length === 0 &&
      summary.attemptsThisWeek >= 10 &&
      (summary.accuracy ?? 0) >= 0.95 &&
      summary.focusMastery >= MASTERY_THRESHOLD
    ) {
      flags.push({
        profileId: member.profileId,
        displayName: member.displayName,
        reason: 'ready-for-more',
        detail: `${Math.round((summary.accuracy ?? 0) * 100)}% accurate on everything set. Ready for harder work.`,
        priority: 40,
      });
    }
  }

  return flags.sort((a, b) => b.priority - a.priority).slice(0, limit);
}

function weakestPrerequisite(
  profile: LearnerProfile,
  registry: CurriculumRegistry,
  conceptId: string,
  now: number,
): { conceptId: string; mastery: number } | undefined {
  const candidates = allPrerequisites(registry.graph, conceptId)
    .filter((id) => registry.concept(id)?.status === 'available')
    .map((id) => ({ conceptId: id, mastery: computeMastery(profile.concepts[id], now).overall }))
    .filter((entry) => entry.mastery < 0.7)
    .sort((a, b) => a.mastery - b.mastery);
  return candidates[0];
}

/* -------------------------------------------------------------------------- */
/* Concept-level view                                                         */
/* -------------------------------------------------------------------------- */

export type ConceptCohortView = {
  conceptId: string;
  title: string;
  /** Mean mastery across every student who has started it. */
  averageMastery: number;
  masteredCount: number;
  strugglingCount: number;
  notStartedCount: number;
  /** Misconception ids seen across the class, most frequent first. */
  commonMisconceptions: { id: string; students: number; occurrences: number }[];
  /**
   * True when enough of the class is stuck that the problem is the teaching
   * rather than the students.
   */
  needsReteaching: boolean;
};

/** Above this share of the class struggling, re-teach rather than remediate. */
export const RETEACH_SHARE = 0.4;

export function conceptCohortView(
  roster: ClassRoster,
  registry: CurriculumRegistry,
  conceptId: string,
  now: number,
): ConceptCohortView {
  let started = 0;
  let masteredCount = 0;
  let strugglingCount = 0;
  let masterySum = 0;

  const byMisconception = new Map<string, { students: number; occurrences: number }>();

  for (const member of roster.members) {
    const state = member.profile.concepts[conceptId];
    if (!state || state.attempts === 0) continue;

    started += 1;
    const mastery = computeMastery(state, now).overall;
    masterySum += mastery;
    if (mastery >= MASTERY_THRESHOLD) masteredCount += 1;
    if (state.attempts >= 3 && mastery < STRUGGLING_THRESHOLD) strugglingCount += 1;

    for (const [id, count] of Object.entries(state.misconceptions)) {
      const entry = byMisconception.get(id) ?? { students: 0, occurrences: 0 };
      entry.students += 1;
      entry.occurrences += count;
      byMisconception.set(id, entry);
    }
  }

  const commonMisconceptions = [...byMisconception.entries()]
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => b.students - a.students || b.occurrences - a.occurrences)
    .slice(0, 4);

  return {
    conceptId,
    title: title(registry, conceptId),
    averageMastery: started === 0 ? 0 : masterySum / started,
    masteredCount,
    strugglingCount,
    notStartedCount: roster.members.length - started,
    commonMisconceptions,
    // Judged against the students who have actually attempted it: a concept
    // nobody has started is not a teaching failure.
    needsReteaching: started >= 3 && strugglingCount / started >= RETEACH_SHARE,
  };
}

/* -------------------------------------------------------------------------- */
/* Whole-class overview                                                       */
/* -------------------------------------------------------------------------- */

export type ClassOverview = {
  studentCount: number;
  activeThisWeek: number;
  averageMastery: number;
  /** Concepts where enough of the class is stuck to warrant re-teaching. */
  reteachConceptIds: string[];
  /** The single misconception most widespread across the class. */
  topMisconception: { id: string; students: number } | undefined;
  concepts: ConceptCohortView[];
};

export function classOverview(roster: ClassRoster, registry: CurriculumRegistry, now: number): ClassOverview {
  const concepts = roster.focusConceptIds.map((conceptId) =>
    conceptCohortView(roster, registry, conceptId, now),
  );

  const activeThisWeek = roster.members.filter((member) =>
    member.profile.attempts.some((attempt) => now - attempt.at <= WEEK_MS && attempt.status !== 'malformed'),
  ).length;

  const averageMastery =
    roster.members.length === 0
      ? 0
      : roster.members.reduce(
          (sum, member) => sum + aggregateMastery(roster.focusConceptIds, member.profile.concepts, now),
          0,
        ) / roster.members.length;

  const spread = new Map<string, number>();
  for (const concept of concepts) {
    for (const entry of concept.commonMisconceptions) {
      spread.set(entry.id, (spread.get(entry.id) ?? 0) + entry.students);
    }
  }
  const [topId, topStudents] = [...spread.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];

  return {
    studentCount: roster.members.length,
    activeThisWeek,
    averageMastery,
    reteachConceptIds: concepts.filter((c) => c.needsReteaching).map((c) => c.conceptId),
    topMisconception: topId ? { id: topId, students: topStudents as number } : undefined,
    concepts,
  };
}

/**
 * Groups students by readiness for one concept, so a teacher can plan a lesson
 * rather than read a table. Three groups is what fits on a whiteboard.
 */
export type ReadinessGroups = {
  ready: StudentSummary[];
  needsSupport: StudentSummary[];
  needsPrerequisite: StudentSummary[];
};

export function groupByReadiness(
  roster: ClassRoster,
  registry: CurriculumRegistry,
  conceptId: string,
  now: number,
): ReadinessGroups {
  const groups: ReadinessGroups = { ready: [], needsSupport: [], needsPrerequisite: [] };

  for (const member of roster.members) {
    const summary = summariseStudent(member, [conceptId], now);
    const blocked = weakestPrerequisite(member.profile, registry, conceptId, now);

    if (blocked) groups.needsPrerequisite.push(summary);
    else if (summary.strugglingConceptIds.length > 0) groups.needsSupport.push(summary);
    else groups.ready.push(summary);
  }

  return groups;
}

const title = (registry: CurriculumRegistry, conceptId: string): string =>
  registry.concept(conceptId)?.title ?? conceptId;

export type MasteryCell = { profileId: string; conceptId: string; mastery: MasteryBreakdown };

/**
 * The mastery grid: every student against every focus concept.
 *
 * Returned as a flat list rather than a matrix so the caller decides the
 * orientation — teachers read by student, coordinators read by concept.
 */
export function masteryGrid(roster: ClassRoster, now: number): MasteryCell[] {
  return roster.members.flatMap((member) =>
    roster.focusConceptIds.map((conceptId) => ({
      profileId: member.profileId,
      conceptId,
      mastery: computeMastery(member.profile.concepts[conceptId], now),
    })),
  );
}
